import { Song, ResolveDownloadTokenResponse } from '@maso-dj/shared-types';
import { logger } from '@maso-dj/shared-types';

interface CachedUrl {
  url: string;
  expiresAt: number; // Unix timestamp in milliseconds
}

export class AudioPlayer {
  private audio: HTMLAudioElement;
  private fadeOutInterval: number | null = null;
  private fadeInInterval: number | null = null;
  private onSongEndCallback: (() => void) | null = null;
  private fadeoutDuration: number = 5; // Default 5 seconds
  private baseVolume: number = 1.0; // Default 100% volume
  private endTimeCheckInterval: number | null = null; // For checking if we've reached endTime
  private currentSongEndTime: number | null = null; // Track the end time for current song
  private urlCache: Map<string, CachedUrl> = new Map(); // Cache resolved URLs

  constructor() {
    this.audio = new Audio();
    this.audio.addEventListener('ended', () => {
      if (this.onSongEndCallback) {
        this.onSongEndCallback();
      }
    });

    // Add detailed error logging
    this.audio.addEventListener('error', (e) => {
      const error = this.audio.error;
      logger.error('Audio element error:', {
        code: error?.code,
        message: error?.message,
        src: this.audio.src,
        event: e
      });
    });

    this.audio.addEventListener('loadstart', () => {
      logger.debug('Audio loading started:', this.audio.src);
    });

    this.audio.addEventListener('canplay', () => {
      logger.debug('Audio can play:', this.audio.src);
    });

    this.audio.addEventListener('playing', () => {
      logger.debug('Audio is playing:', this.audio.src);
    });

    this.audio.addEventListener('pause', () => {
      logger.debug('Audio paused');
    });

    this.audio.addEventListener('stalled', () => {
      logger.warn('Audio stalled');
    });

    this.audio.addEventListener('suspend', () => {
      logger.debug('Audio suspended');
    });
  }

  public setFadeoutDuration(seconds: number): void {
    this.fadeoutDuration = seconds;
  }

  public setVolume(volume: number): void {
    this.baseVolume = Math.max(0, Math.min(1, volume));
    if (!this.isFading()) {
      this.audio.volume = this.baseVolume;
    }
  }

  public getVolume(): number {
    return this.baseVolume;
  }

  public async play(song: Song): Promise<void> {
    logger.debug('AudioPlayer.play() called with song:', {
      title: song.title,
      url: song.url,
      downloadToken: song.downloadToken,
      source: song.source,
      startTime: song.startTime,
      endTime: song.endTime
    });

    // Resolve download token to URL if present (just-in-time URL generation)
    let songUrl = song.url;
    if (!songUrl && song.downloadToken) {
      try {
        logger.debug('Resolving download token:', song.downloadToken);
        songUrl = await this.resolveDownloadToken(song.downloadToken);
        logger.debug('Download token resolved to URL:', songUrl);
      } catch (error) {
        logger.error('Failed to resolve download token:', error);
        throw new Error(`Failed to load song: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    if (!songUrl) {
      logger.error('Cannot play song without URL or download token:', song.title);
      return;
    }

    this.stop();
    this.audio.src = songUrl;
    logger.debug('Audio src set to:', this.audio.src);

    // Set volume to base volume when starting playback
    this.audio.volume = this.baseVolume;

    // Store the end time for this song
    this.currentSongEndTime = song.endTime ?? null;

    // Get start time before loading
    const startTime = song.startTime ?? 0;

    // Wait for audio to be loaded enough to seek
    const onCanPlay = (): void => {
      this.audio.removeEventListener('canplay', onCanPlay);

      // Set start time after audio is ready
      if (startTime > 0) {
        this.audio.currentTime = startTime;
        logger.debug('Set audio start time to:', startTime);
      }

      // Now start playback
      this.audio.play()
        .then(() => {
          logger.debug('Audio play() promise resolved');

          // Start monitoring end time if specified
          if (this.currentSongEndTime !== null) {
            this.startEndTimeMonitoring();
          }
        })
        .catch((error) => {
          logger.error('Error playing audio:', error);
          logger.error('Error details:', {
            name: error.name,
            message: error.message,
            audioSrc: this.audio.src,
            audioReadyState: this.audio.readyState,
            audioNetworkState: this.audio.networkState
          });
        });
    };

    this.audio.addEventListener('canplay', onCanPlay);
    this.audio.load();
    logger.debug('Audio load() called');
  }

  /**
   * Resolve a download token to a presigned URL
   * Makes API call to /api/files/resolve-token
   * Caches URLs to avoid redundant API calls for the same token
   */
  private async resolveDownloadToken(downloadToken: string): Promise<string> {
    const now = Date.now();

    // Check cache for unexpired URL (with 30s safety buffer)
    const cached = this.urlCache.get(downloadToken);
    if (cached && cached.expiresAt > now + 30000) {
      logger.debug('Using cached URL for token:', downloadToken);
      return cached.url;
    }

    // Import authenticatedFetch dynamically to avoid circular dependencies
    const { authenticatedFetch } = await import('../services/AuthenticatedFetch.js');

    const response = await authenticatedFetch('/api/files/resolve-token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ downloadToken })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to resolve download token: ${response.status} ${errorText}`);
    }

    const result: ResolveDownloadTokenResponse = await response.json();
    if (!result.success || !result.url) {
      throw new Error('Invalid response from token resolution endpoint');
    }

    // Cache URL with expiry
    this.urlCache.set(downloadToken, {
      url: result.url,
      expiresAt: now + (result.expiresIn * 1000)
    });

    logger.debug('Cached new URL for token:', downloadToken, 'expires in', result.expiresIn, 'seconds');

    return result.url;
  }

  public stop(): void {
    this.stopEndTimeMonitoring();
    if (this.fadeOutInterval) {
      clearInterval(this.fadeOutInterval);
      this.fadeOutInterval = null;
    }

    // Clear the audio source to fully stop loading/playback
    this.audio.pause();
    this.audio.src = '';
    this.audio.load(); // Reset the media element
    this.audio.volume = this.baseVolume;
    this.currentSongEndTime = null;
  }

  public fadeOutAndStop(callback?: () => void, progressCallback?: (progress: number) => void): void {
    // If fadeout duration is 0, stop immediately
    if (this.fadeoutDuration === 0) {
      this.stop();
      if (callback) {
        callback();
      }
      return;
    }

    const fadeDuration = this.fadeoutDuration * 1000; // Convert to milliseconds
    const fadeSteps = 50;
    const stepDuration = fadeDuration / fadeSteps;
    const volumeStep = this.baseVolume / fadeSteps;
    let currentStep = 0;

    // Clear any existing fade intervals
    if (this.fadeOutInterval) {
      clearInterval(this.fadeOutInterval);
    }
    if (this.fadeInInterval) {
      clearInterval(this.fadeInInterval);
      this.fadeInInterval = null;
    }

    this.fadeOutInterval = window.setInterval(() => {
      currentStep++;
      const newVolume = Math.max(0, this.baseVolume - (volumeStep * currentStep));
      this.audio.volume = newVolume;

      // Report progress (0 to 1)
      if (progressCallback) {
        progressCallback(currentStep / fadeSteps);
      }

      if (currentStep >= fadeSteps || newVolume <= 0) {
        this.stop();
        if (callback) {
          callback();
        }
      }
    }, stepDuration);
  }

  public abortFadeout(fadeInDuration?: number, progressCallback?: (progress: number) => void, callback?: () => void): void {
    if (!this.fadeOutInterval) {
      return; // No fadeout in progress
    }

    // Clear the fadeout interval
    clearInterval(this.fadeOutInterval);
    this.fadeOutInterval = null;

    // Fade back to base volume
    const currentVolume = this.audio.volume;
    const volumeDiff = this.baseVolume - currentVolume;

    if (volumeDiff <= 0) {
      // Already at or above base volume
      this.audio.volume = this.baseVolume;
      if (callback) {
        callback();
      }
      return;
    }

    // Use half the fadeout duration if not specified
    const fadeInTime = fadeInDuration ?? (this.fadeoutDuration / 2);
    const fadeDuration = fadeInTime * 1000; // Convert to milliseconds
    const fadeSteps = 50;
    const stepDuration = fadeDuration / fadeSteps;
    const volumeStep = volumeDiff / fadeSteps;
    let currentStep = 0;

    this.fadeInInterval = window.setInterval(() => {
      currentStep++;
      const newVolume = Math.min(this.baseVolume, currentVolume + (volumeStep * currentStep));
      this.audio.volume = newVolume;

      // Report progress (1 to 0, where 0 is fully restored)
      if (progressCallback) {
        progressCallback(1 - (currentStep / fadeSteps));
      }

      if (currentStep >= fadeSteps || newVolume >= this.baseVolume) {
        if (this.fadeInInterval) {
          clearInterval(this.fadeInInterval);
          this.fadeInInterval = null;
        }
        this.audio.volume = this.baseVolume;
        if (callback) {
          callback();
        }
      }
    }, stepDuration);
  }

  public isFading(): boolean {
    return this.fadeOutInterval !== null;
  }

  public stopImmediately(callback?: () => void): void {
    this.stop();
    if (callback) {
      callback();
    }
  }

  public isPlaying(): boolean {
    return !this.audio.paused && !this.audio.ended;
  }

  public onSongEnd(callback: () => void): void {
    this.onSongEndCallback = callback;
  }

  public getCurrentTime(): number {
    return this.audio.currentTime;
  }

  public getDuration(): number {
    return this.audio.duration;
  }

  private startEndTimeMonitoring(): void {
    // Check every 100ms if we've reached the end time
    this.stopEndTimeMonitoring(); // Clear any existing interval

    this.endTimeCheckInterval = window.setInterval(() => {
      if (this.currentSongEndTime !== null && this.audio.currentTime >= this.currentSongEndTime) {
        logger.debug('Reached song end time:', this.currentSongEndTime);
        this.stopEndTimeMonitoring();
        // Trigger the song end callback
        if (this.onSongEndCallback) {
          this.onSongEndCallback();
        }
      }
    }, 100);
  }

  private stopEndTimeMonitoring(): void {
    if (this.endTimeCheckInterval) {
      clearInterval(this.endTimeCheckInterval);
      this.endTimeCheckInterval = null;
    }
  }
}
